import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';

import { TableConfig } from '@app/shared/util/table-config.utils';

import { Ticket } from '@models/ticket.model';
import { TranslateService } from "@ngx-translate/core";
import { TicketStatus } from "@models/enums/ticket-status";
import { Log } from "ng2-logger";
import { ConfirmationModalComponent, ToolScanComponent } from "@components/index";
import { TicketService } from "@services/ticket.service";
import { ToastsManager } from "@app/shared/util/toastr.service";
import * as _ from 'lodash';
import { LineService } from '@app/core/services/line.service';
import { map } from "rxjs/operators";
import { TableComponent } from "@components/custom-table/table.component";

@Component({
    selector: 'app-service-tickets-table',
    templateUrl: './service-tickets-table.component.html',
    styleUrls: ['./service-tickets-table.component.scss']
})
export class ServiceTicketsTableComponent implements OnInit {

    @ViewChild(TableComponent, {static: false}) table: TableComponent;

    _logger = Log.create(ServiceTicketsTableComponent.name);
    filterStatusList = [];
    filterLocationList = [];

    @Input() tickets: Ticket[] = [];
    @Input() tableConfig: TableConfig;
    @Input() isLoading = false;
    @Input() headerBackgroundColor: string;
    @Input() hasFilters = true;
    @Input() hasTitle = true;
    @Input() isMaterial = true;

    @Output() pageChange = new EventEmitter<any>();
    @Output() filterChange = new EventEmitter<any>();
    @Output() sortChange = new EventEmitter<any>();
    @Output() rowClick = new EventEmitter<any>();

    @ViewChild(ConfirmationModalComponent, {static: true}) confirmationModal: ConfirmationModalComponent;
    @ViewChild(ToolScanComponent, {static: true}) toolScan: ToolScanComponent;

    public ticket: Ticket = new Ticket();

    public toolName: string;
    public scanDefaultValue: string;
    onNameChanged = _.debounce(this.nameChanged, 300);
    onToolScan = _.debounce(this.onScan, 300);
    nextAction: string;
    storageFilters = 'serviceTickets.table.filters';
    lineSelectedValue: number;
    linesItems: any[];

    constructor(
        private translateService: TranslateService,
        private toastr: ToastsManager,
        private lineService: LineService,
        private ticketService: TicketService) {

        this.filterStatusList = this.buildStatusFilterColumnKeys();
        this.filterLocationList = this.buildLocationFilterColumnKeys();

        this.translateService.onLangChange.subscribe((event) => {
            this.filterStatusList = this.buildStatusFilterColumnKeys();
            this.filterLocationList = this.buildLocationFilterColumnKeys();
        });
    }

    ngOnInit() {
        this.getLines();
    }

    onPageChange(tableConfig: TableConfig) {
        this._logger.info('onPageChange', this.tableConfig);
        this.pageChange.emit(tableConfig);
    }

    onSortChange(tableConfig: TableConfig) {
        this._logger.info('onSortChange', this.tableConfig);
        this.sortChange.emit(tableConfig);
    }

    nameChanged() {
        const name = "toolName";
        if (!this.toolName) {
            this.tableConfig.removeFilter(name);
            sessionStorage.removeItem(this.storageFilters + name);
            this.filterChange.emit(this.tableConfig);
            return;
        }

        this.tableConfig.upsertFilter(name, this.toolName);
        this.filterChange.emit(this.tableConfig);
        sessionStorage.setItem(this.storageFilters + name, this.toolName);
    }

    onFilterChange() {
        this._logger.info('onFilterChange', this.tableConfig);
        this.setSavedFilters();
        this.filterChange.emit(this.tableConfig);
    }

    onLocationFilterChange(column: any, table: any) {
        if (column.filterValue === 'all') {
            column.filterValue = '';
        }
        table.onFilterChange(column);
    }

    onStatusFilterChange(column: any, table: any) {
        if (column.filterValue === 'all' || column.filterValue === 'All') {
            column.filterValue = '';
        }
        table.onFilterChange(column);
    }

    onRequestTools(ticketID: number) {
        this.getTicketDetails(ticketID);
        this.confirmationModal.open({
            action: 'requestTools'
        });
        this.nextAction = 'requestTools';
    }

    onScan(toolUid: string) {

        const name = 'toolUid';
        if (!toolUid) {
            this.tableConfig.removeFilter(name);
            sessionStorage.removeItem(this.storageFilters + name);
            this.filterChange.emit(this.tableConfig);
            return;
        }


        if (this.tableConfig.sameValue(name, toolUid)) {
            return;
        }

        this.tableConfig.upsertFilter(name, toolUid);
        this.filterChange.emit(this.tableConfig);
        sessionStorage.setItem(this.storageFilters + name, toolUid);
    }

    getTicketDetails(ticketId: number) {
        this._logger.info("getTicketDetails:: Retrieving details for ticket with id", ticketId);
        this.ticketService.getTicketById(ticketId).subscribe(
            (data: Ticket) => {
                this._logger.info('getTicketById:: API Returned data', data);
                this.ticket = data;
            },
            (error) => {
                this._logger.info('getTicketById:: API Returned error', error);
            }
        );
    }

    onScanBadge(event) {
        this.rowClick.emit(this.ticket);
        this.confirmRequestTools(this.ticket, event.boschId)
    }

    confirmRequestTools(ticket, boschId) {
        this._logger.info("confirmRequestTools:: Requesting tools for service ticket");
        this.ticketService.requestTools(ticket, boschId).subscribe(
            (data) => {
                this.confirmationModal.close();
                this._logger.info('requestTools:: API Returned data', data);
                this.getTicketDetails(this.ticket.id);
                this.toastr.success(this.buildRequestToolsMessage());
            },
            (error) => {
                this.confirmationModal.close();
                this._logger.error('requestTools:: API Returned error', error);
            }
        );
    };

    onResumeProcess(ticket) {
        this.rowClick.emit(ticket);
    }

    buildRequestToolsMessage() {
        return this.translateService.instant('service.tickets.details.toasts.toolsRequested',
            {ticketId: this.ticket.id});
    }

    buildStatusFilterColumnKeys() {
        return [
            {name: this.translateService.instant("generic.statuses.all"), value: 'all'},
            {name: this.translateService.instant("generic.statuses.new"), value: TicketStatus.NEW},
            {name: this.translateService.instant("generic.statuses.waitingForTools"), value: TicketStatus.WAITING_FOR_TOOLS},
            {name: this.translateService.instant("generic.statuses.ready"), value: TicketStatus.READY},
            {name: this.translateService.instant("generic.statuses.inProgress"), value: TicketStatus.IN_PROGRESS},
            {name: this.translateService.instant('generic.statuses.onHold'), value: TicketStatus.ON_HOLD},
        ];
    }

    private buildLocationFilterColumnKeys() {
        return [
            {name: this.translateService.instant('generic.statuses.all'), value: 'all'},
            {name: this.translateService.instant('service.tickets.table.serviceArea'), value: 'false'},
            {name: this.translateService.instant('service.tickets.table.productionLine'), value: 'true'}
        ];
    }

    onGroupFilterChanged(line: any) {
        this.setSavedFilters();
        if (line.id) {
            this.tableConfig.upsertFilter("mainLineId", line.id);
        } else {
            this.tableConfig.removeFilter("mainLineId");
        }
        this.filterChange.emit(this.tableConfig);
    }

    private getLines() {
        this.lineService.getAllLines().pipe(
            map(res => this.addAllOptionForSelect(res, 'items')))
            .subscribe(
            response => {
                this.linesItems = response.items;
            },
            error => {
                this._logger.error("getLines::", error);
            },
        )
    }

    private addAllOptionForSelect(response, itemsName) {
        response[itemsName].unshift({
            id: '',
            name: this.translateService.instant('admin.tools.table.groupFilter.all.' + itemsName)
        });
        response.total++;

        return response;
    }

    get modalTitle(): string {
        return this.translateService.instant('service.tickets.details.confirmation-modal.' + this.nextAction + '.popupTitle');
    }

    get modalQuestion(): string {
        return this.translateService.instant('service.tickets.details.confirmation-modal.' + this.nextAction + '.popupQuestion',
            {currentOperation: this.ticket.currentOperation.name});
    }

    get modalMessage(): string {
        return this.translateService.instant('service.tickets.details.confirmation-modal.' + this.nextAction + '.popupMessage');
    }

    private setSavedFilters() {
        if (!this.tableConfig) {
            this.tableConfig = new TableConfig();
        }
        this.toolName = sessionStorage.getItem(this.storageFilters + 'toolName') || '';
        this.scanDefaultValue = sessionStorage.getItem(this.storageFilters + 'toolUid') || '';
        this.tableConfig.upsertFilter("toolName", this.toolName);
        this.tableConfig.upsertFilter("toolUid", this.scanDefaultValue);
    }

    toggleExpand(expand: boolean) {
        this.table.onExpandAll(expand);
    }
}
